import { useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Printer, Copy, Download, ScanLine, Check } from "lucide-react";

/**
 * CheckInQrCode — auto-generates a QR code that points guests directly to /event/checkin.
 * Includes copy-link, download-PNG, and a full-page print sign optimized for 8.5x11 paper.
 */
export default function CheckInQrCode() {
  const [copied, setCopied] = useState(false);
  const svgWrapRef = useRef(null);

  const url = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/event/checkin`;
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  const downloadPng = () => {
    const svg = svgWrapRef.current?.querySelector("svg");
    if (!svg) return;
    // Serialize SVG → rasterize to PNG at 1024x1024
    const clone = svg.cloneNode(true);
    clone.setAttribute("width", "1024");
    clone.setAttribute("height", "1024");
    const xml = new XMLSerializer().serializeToString(clone);
    const svg64 = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 1024;
      canvas.height = 1024;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 1024, 1024);
      ctx.drawImage(img, 0, 0, 1024, 1024);
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = "business-showers-checkin-qr.png";
      a.click();
    };
    img.src = svg64;
  };

  const printSign = () => {
    const w = window.open("", "_blank", "width=816,height=1056");
    if (!w) return;
    const svg = svgWrapRef.current?.querySelector("svg");
    if (!svg) return;
    const clone = svg.cloneNode(true);
    clone.removeAttribute("width");
    clone.removeAttribute("height");
    clone.setAttribute("style", "width: 380px; height: 380px;");
    const xml = new XMLSerializer().serializeToString(clone);

    w.document.write(`<!DOCTYPE html>
<html>
<head>
<title>Business Showers · Check-In</title>
<style>
  @page { size: letter portrait; margin: 0.5in; }
  * { box-sizing: border-box; }
  body {
    margin: 0; padding: 0.75in 0.5in;
    font-family: 'Cormorant Garamond', Georgia, serif;
    color: #052e16; background: #ffffff;
    display: flex; flex-direction: column; align-items: center; text-align: center;
    min-height: 10in;
  }
  .eyebrow { font-family: 'Outfit', sans-serif; font-size: 12px; letter-spacing: 0.28em; text-transform: uppercase; color: #A50E52; font-weight: 800; margin-bottom: 8px; }
  h1 { font-size: 42px; line-height: 1.05; margin: 0 0 6px; }
  .theme { font-size: 22px; color: #7E0A3D; font-style: italic; margin: 0 0 4px; }
  .dates { font-family: 'Outfit', sans-serif; font-size: 14px; color: #14532d; margin: 0 0 24px; letter-spacing: 0.05em; }
  .qr-frame {
    border: 4px solid #052e16;
    padding: 20px;
    border-radius: 24px;
    background: #ffffff;
    margin: 6px 0 20px;
    box-shadow: 0 0 0 8px #ffffff, 0 0 0 12px #A50E52;
  }
  .cta { font-family: 'Outfit', sans-serif; font-size: 26px; font-weight: 800; letter-spacing: 0.02em; margin: 6px 0 4px; color: #052e16; }
  .cta strong { color: #A50E52; }
  .instruct { font-family: 'Outfit', sans-serif; font-size: 14px; color: #052e16; opacity: 0.7; margin: 4px 0 22px; }
  .steps { font-family: 'Outfit', sans-serif; font-size: 13px; color: #052e16; margin: 0 0 20px; max-width: 5in; }
  .steps ol { padding-left: 20px; text-align: left; }
  .steps li { margin-bottom: 4px; }
  .footer { position: absolute; bottom: 0.5in; font-family: 'Outfit', sans-serif; font-size: 11px; color: #052e16; opacity: 0.6; letter-spacing: 0.1em; }
  .url { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #14532d; opacity: 0.6; margin-top: 6px; word-break: break-all; }
</style>
</head>
<body>
  <div class="eyebrow">2026 · Fast Forward</div>
  <h1>Freedom In Me<br/>Business Showers</h1>
  <div class="dates">July 17–18, 2026 · Charlotte, NC</div>
  <div class="qr-frame">${xml}</div>
  <div class="cta">Scan to <strong>Check In</strong></div>
  <div class="instruct">Point your phone camera at the code — no app needed.</div>
  <div class="steps">
    <ol>
      <li>Open your phone camera and hover it over the code.</li>
      <li>Tap the banner that appears at the top of the screen.</li>
      <li>Enter your email, name, or phone — we'll find you.</li>
      <li>Confirm and you're in!</li>
    </ol>
  </div>
  <div class="url">${url}</div>
  <div class="footer">Business Showers · fimbusinessshowers.org</div>
  <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
</body>
</html>`);
    w.document.close();
  };

  return (
    <div data-testid="checkin-qr-block" className="rounded-2xl border-2 border-emerald-950 bg-white p-5 text-center">
      <div className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-50 border border-fuchsia-200 px-3 py-1 mb-3">
        <ScanLine size={12} className="text-fuchsia-700" />
        <span className="text-[10px] tracking-[0.2em] uppercase font-[Outfit] font-bold text-fuchsia-800">
          Fast Check-In
        </span>
      </div>
      <h3 className="font-[Cormorant_Garamond] text-2xl leading-tight text-emerald-950 mb-1">
        Scan to check in
      </h3>
      <p className="font-[Outfit] text-xs text-emerald-950/60 mb-4 max-w-xs mx-auto">
        Point your phone camera at the code — no typing, no app.
      </p>

      <div ref={svgWrapRef} className="mx-auto inline-block bg-white p-3 rounded-xl border-4 border-emerald-950 shadow-lg" data-testid="checkin-qr-svg">
        <QRCodeSVG
          value={url || "https://businessshowers.org"}
          size={220}
          level="H"
          bgColor="#ffffff"
          fgColor="#052e16"
          marginSize={2}
        />
      </div>

      <div className="mt-3 text-[11px] font-[Outfit] font-mono text-emerald-950/50 break-all">
        {url}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <Button
          data-testid="checkin-qr-copy"
          onClick={copy}
          variant="outline"
          className="rounded-xl h-10 border-2 border-emerald-950/10 bg-white text-emerald-950 font-[Outfit] font-bold text-xs hover:border-fuchsia-400"
        >
          {copied ? <><Check size={14} className="mr-1" /> Copied</> : <><Copy size={14} className="mr-1" /> Copy link</>}
        </Button>
        <Button
          data-testid="checkin-qr-download"
          onClick={downloadPng}
          variant="outline"
          className="rounded-xl h-10 border-2 border-emerald-950/10 bg-white text-emerald-950 font-[Outfit] font-bold text-xs hover:border-fuchsia-400"
        >
          <Download size={14} className="mr-1" /> PNG
        </Button>
        <Button
          data-testid="checkin-qr-print"
          onClick={printSign}
          className="rounded-xl h-10 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-[Outfit] font-bold text-xs shadow-md"
        >
          <Printer size={14} className="mr-1" /> Print sign
        </Button>
      </div>

      <p className="mt-3 text-[10px] font-[Outfit] text-emerald-950/40">
        Tip: print the sign, laminate, and tape at each entrance.
      </p>
    </div>
  );
}
